// Tips localStorage management
// Handles persistence of tip dismissal state

const LS_KEY = 'mirukuru.tips';

/**
 * Read the tips state from localStorage
 * @returns {{ dismissed: Record<string, number> }}
 */
export function readTipsState() {
  try {
    const stored = localStorage.getItem(LS_KEY);
    if (!stored) {
      return { dismissed: {} };
    }
    const parsed = JSON.parse(stored);
    // Validate structure
    if (typeof parsed === 'object' && parsed !== null && typeof parsed.dismissed === 'object') {
      return parsed;
    }
    return { dismissed: {} };
  } catch (error) {
    console.warn('[Tips] Failed to read tips state from localStorage:', error);
    return { dismissed: {} };
  }
}

/**
 * Write the tips state to localStorage
 * @param {{ dismissed: Record<string, number> }} state
 */
export function writeTipsState(state) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[Tips] Failed to write tips state to localStorage:', error);
  }
}

/**
 * Get the dismissed version for a specific tip
 * @param {string} tipKey
 * @returns {number} The dismissed version, or 0 if not dismissed
 */
export function getDismissedVersion(tipKey) {
  try {
    const state = readTipsState();
    return state.dismissed[tipKey] || 0;
  } catch (error) {
    console.warn('[Tips] Failed to get dismissed version:', error);
    return 0;
  }
}

/**
 * Dismiss a tip at a specific version
 * @param {string} tipKey
 * @param {number} version
 */
export function dismissTip(tipKey, version) {
  try {
    const state = readTipsState();
    const existingVersion = state.dismissed[tipKey] || 0;
    // Only update if the new version is higher
    state.dismissed[tipKey] = Math.max(existingVersion, version);
    writeTipsState(state);
  } catch (error) {
    console.warn('[Tips] Failed to dismiss tip:', error);
  }
}

/**
 * Check if a tip should be shown based on its version
 * @param {string} tipKey
 * @param {number} version
 * @returns {boolean} True if the tip should be shown
 */
export function shouldShowTip(tipKey, version) {
  try {
    const dismissedVersion = getDismissedVersion(tipKey);
    return dismissedVersion < version;
  } catch (error) {
    console.warn('[Tips] Failed to check if tip should show:', error);
    return true; // Default to showing the tip if there's an error
  }
}

/**
 * Reset all dismissed tips (for debugging/testing)
 */
export function resetAllTips() {
  try {
    writeTipsState({ dismissed: {} });
  } catch (error) {
    console.warn('[Tips] Failed to reset tips:', error);
  }
}
